import React, { useState, useEffect } from 'react'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import Button from '@renderer/components/Button'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useTranslation } from 'react-i18next'
import { useDeviceManager } from '@renderer/hooks'
import cogoToast from '@successtar/cogo-toast'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import {
  detectAvailableSettings,
  type AvailableSettings
} from '@renderer/utils/cameras/cameraSettingsManager'
import { useDesignLayout } from '@renderer/hooks'

type ConfigValue = string | number | boolean
type CameraKey = 'primary' | 'secondary'

type ResizeQuality = 'pixelated' | 'low' | 'medium' | 'high'

interface PreviewQuality {
  maxWidth: number
  maxHeight: number
  quality: number
  resizeQuality: ResizeQuality
}

interface Config {
  name: string
  info: string
  label: string
  readonly: boolean
  type: 'range' | 'menu' | 'radio' | 'toggle' | 'text' | 'window' | 'section' | 'datetime'
  value?: ConfigValue
  min?: number
  max?: number
  step?: number
  choices?: string[]
  children?: Record<string, Config>
}

interface CameraSettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

// Quality presets for the dropdown
const QUALITY_PRESETS = [
  {
    key: 'pixelated',
    label: 'Pixelated (240p)',
    width: 426,
    height: 240,
    quality: 0.4,
    resizeQuality: 'pixelated'
  },
  {
    key: 'low',
    label: 'Low (480p)',
    width: 854,
    height: 480,
    quality: 0.6,
    resizeQuality: 'low'
  },
  {
    key: 'medium',
    label: 'Medium (720p)',
    width: 1280,
    height: 720,
    quality: 0.8,
    resizeQuality: 'medium'
  },
  {
    key: 'high',
    label: 'High (1080p)',
    width: 1920,
    height: 1080,
    quality: 1,
    resizeQuality: 'high'
  },
  {
    key: 'ultra',
    label: 'Ultra (1440p)',
    width: 2560,
    height: 1440,
    quality: 1,
    resizeQuality: 'high'
  }
]

const RATIO_OPTIONS = [
  { label: 'Auto', value: 'auto', key: 'auto' },
  { label: 'Square (1:1)', value: 1 / 1, key: 'square' },
  { label: 'Portrait (3:4)', value: 3 / 4, key: 'portrait' },
  { label: 'Landscape (4:3)', value: 4 / 3, key: 'landscape' },
  { label: 'Wide Portrait (9:16)', value: 9 / 16, key: 'widePortrait' },
  { label: 'Wide Landscape (16:9)', value: 16 / 9, key: 'wideLandscape' },
  { label: 'Classic Portrait (2:3)', value: 2 / 3, key: 'classicPortrait' },
  { label: 'Classic Landscape (3:2)', value: 3 / 2, key: 'classicLandscape' }
]

const CameraConfigWidget: React.FC<{
  config: Config
  onChange: (name: string, value: ConfigValue) => void
}> = ({ config, onChange }): JSX.Element | null => {
  const { t } = useTranslation()
  const { label, name, type, value, readonly, min, max, step, choices } = config

  const handleChange = (newValue: ConfigValue): void => {
    onChange(name, newValue)
  }

  switch (type) {
    case 'range':
      return (
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">
            {t('cameraConfig.label', label)}
          </label>
          <input
            type="number"
            value={value as number}
            min={min}
            max={max}
            step={step}
            onChange={(e) => handleChange(parseFloat(e.target.value))}
            disabled={readonly}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            title={t('cameraConfig.label', label)}
            aria-label={t('cameraConfig.label', label)}
          />
        </div>
      )

    case 'menu':
    case 'radio':
      return (
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">{label}</label>
          <select
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            disabled={readonly}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            title={label}
            aria-label={label}
          >
            {choices?.map((choice, index) => (
              <option key={`${name}-${choice}-${index}`} value={choice}>
                {choice}
              </option>
            ))}
          </select>
        </div>
      )

    case 'toggle':
      return (
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">{label}</label>
          <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
            <span className="text-sm">{label}</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={value as boolean}
                onChange={(e) => handleChange(e.target.checked)}
                disabled={readonly}
                aria-label={`Toggle ${label}`}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
            </label>
          </div>
        </div>
      )

    case 'text':
      return (
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">{label}</label>
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            disabled={readonly}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            title={label}
            aria-label={label}
          />
        </div>
      )

    case 'window':
    case 'section':
      if (!config.children) return null
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">{label}</h3>
          <div className="space-y-4 pl-4">
            {Object.entries(config.children).map(([childName, childConfig]) => (
              <CameraConfigWidget
                key={`${name}-${childName}`}
                config={childConfig}
                onChange={onChange}
              />
            ))}
          </div>
        </div>
      )

    default:
      return null
  }
}

const CameraSettingsModal: React.FC<CameraSettingsModalProps> = ({
  isOpen,
  onClose
}): JSX.Element | null => {
  const { t } = useTranslation()
  const { isCompact: camDesignCompact, isPortrait: camDesignPortrait } = useDesignLayout()
  const camModalDense = camDesignCompact && camDesignPortrait
  const {
    cameraSettings,
    setCameraSettings,
    secondaryCameraSettings,
    setSecondaryCameraSettings,
    selectedCamera,
    setSelectedCamera,
    selectedSecondaryCamera,
    setSelectedSecondaryCamera,
    useWebcam,
    setUseWebcam,
    ejectCameraWhenIdle,
    setEjectCameraWhenIdle,
    showCameraQualityWarnings,
    setShowCameraQualityWarnings
  } = useSettingsStore()
  const { devices, fetchDevices } = useDeviceManager()
  const { type: subscriptionType } = useSubscriptionStore()

  const [activeTab, setActiveTab] = useState<CameraKey>('primary')

  const [tempSettings, setTempSettings] = useState(cameraSettings)
  const [tempSecondarySettings, setTempSecondarySettings] = useState(secondaryCameraSettings)
  const [cameraConfig, setCameraConfig] = useState<Config | null>(null)
  const [secondaryCameraConfig, setSecondaryCameraConfig] = useState<Config | null>(null)
  const [availableSettings, setAvailableSettings] = useState<AvailableSettings | null>(null)
  const [secondaryAvailableSettings, setSecondaryAvailableSettings] =
    useState<AvailableSettings | null>(null)

  const [selectedQualityPreset, setSelectedQualityPreset] = useState(() => {
    // Find matching preset or default to custom
    // Add null checks to prevent errors
    const preset = QUALITY_PRESETS.find(
      (p) =>
        cameraSettings.previewQuality &&
        p.width === cameraSettings.previewQuality.maxWidth &&
        p.height === cameraSettings.previewQuality.maxHeight &&
        p.quality === cameraSettings.previewQuality.quality
    )
    return preset?.key || 'custom'
  })

  const [selectedCaptureQualityPreset, setSelectedCaptureQualityPreset] = useState(() => {
    // Find matching preset for capture quality or default to custom
    const preset = QUALITY_PRESETS.find(
      (p) =>
        cameraSettings.captureQuality &&
        p.width === cameraSettings.captureQuality.maxWidth &&
        p.height === cameraSettings.captureQuality.maxHeight &&
        p.quality === cameraSettings.captureQuality.quality
    )
    return preset?.key || 'custom'
  })

  const [selectedSecondaryQualityPreset, setSelectedSecondaryQualityPreset] = useState(() => {
    const preset = QUALITY_PRESETS.find(
      (p) =>
        secondaryCameraSettings.previewQuality &&
        p.width === secondaryCameraSettings.previewQuality.maxWidth &&
        p.height === secondaryCameraSettings.previewQuality.maxHeight &&
        p.quality === secondaryCameraSettings.previewQuality.quality
    )
    return preset?.key || 'custom'
  })

  const [selectedSecondaryCaptureQualityPreset, setSelectedSecondaryCaptureQualityPreset] =
    useState(() => {
      const preset = QUALITY_PRESETS.find(
        (p) =>
          secondaryCameraSettings.captureQuality &&
          p.width === secondaryCameraSettings.captureQuality.maxWidth &&
          p.height === secondaryCameraSettings.captureQuality.maxHeight &&
          p.quality === secondaryCameraSettings.captureQuality.quality
      )
      return preset?.key || 'custom'
    })

  // Helper function to ensure previewQuality always exists in tempSettings
  const ensurePreviewQuality = (
    settings: Partial<typeof tempSettings>
  ): settings is typeof tempSettings & { previewQuality: NonNullable<PreviewQuality> } => {
    if (!settings.previewQuality) {
      settings.previewQuality = {
        maxWidth: 640,
        maxHeight: 480,
        quality: 0.8,
        resizeQuality: 'medium'
      }
    }
    return true
  }

  // Helper function to ensure captureQuality always exists in tempSettings
  const ensureCaptureQuality = (
    settings: Partial<typeof tempSettings>
  ): settings is typeof tempSettings & { captureQuality: NonNullable<PreviewQuality> } => {
    if (!settings.captureQuality) {
      settings.captureQuality = {
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 1.0,
        resizeQuality: 'high'
      }
    }
    return true
  }

  useEffect(() => {
    const fetchCameraConfig = async (cameraKey: CameraKey): Promise<void> => {
      try {
        const config = await unifiedCameraUtils.getConfig(cameraKey)
        if (config) {
          if (cameraKey === 'primary') {
            setCameraConfig(config as Config)
          } else {
            setSecondaryCameraConfig(config as Config)
          }
        }
      } catch (error) {
        console.error(`Failed to fetch ${cameraKey} camera config:`, error)
      }
    }
    if (isOpen) {
      fetchDevices()
      // Initialize temp settings with defaults
      const initialSettings = { ...cameraSettings }
      if (!initialSettings.previewQuality) {
        initialSettings.previewQuality = {
          maxWidth: 640,
          maxHeight: 480,
          quality: 0.8,
          resizeQuality: 'medium'
        }
      }
      if (!initialSettings.captureQuality) {
        initialSettings.captureQuality = {
          maxWidth: 1920,
          maxHeight: 1080,
          quality: 1.0,
          resizeQuality: 'high'
        }
      }
      setTempSettings(initialSettings)

      const initialSecondarySettings = { ...secondaryCameraSettings }
      if (!initialSecondarySettings.previewQuality) {
        initialSecondarySettings.previewQuality = {
          maxWidth: 640,
          maxHeight: 480,
          quality: 0.8,
          resizeQuality: 'medium'
        }
      }
      if (!initialSecondarySettings.captureQuality) {
        initialSecondarySettings.captureQuality = {
          maxWidth: 1920,
          maxHeight: 1080,
          quality: 1.0,
          resizeQuality: 'high'
        }
      }
      setTempSecondarySettings(initialSecondarySettings)

      if (selectedCamera) {
        fetchCameraConfig('primary')
        // Detect available settings for live view brightness feature
        detectAvailableSettings('primary').then((available) => {
          setAvailableSettings(available)
        })
      }
      if (selectedSecondaryCamera) {
        fetchCameraConfig('secondary')
        // Detect available settings for live view brightness feature
        detectAvailableSettings('secondary').then((available) => {
          setSecondaryAvailableSettings(available)
        })
      }

      // Set quality preset
      const preset = QUALITY_PRESETS.find(
        (p) =>
          initialSettings.previewQuality &&
          p.width === initialSettings.previewQuality.maxWidth &&
          p.height === initialSettings.previewQuality.maxHeight &&
          p.quality === initialSettings.previewQuality.quality
      )
      setSelectedQualityPreset(preset?.key || 'custom')
      // Set capture quality preset
      const capturePreset = QUALITY_PRESETS.find(
        (p) =>
          initialSettings.captureQuality &&
          p.width === initialSettings.captureQuality.maxWidth &&
          p.height === initialSettings.captureQuality.maxHeight &&
          p.quality === initialSettings.captureQuality.quality
      )
      setSelectedCaptureQualityPreset(capturePreset?.key || 'custom')

      const secondaryPreset = QUALITY_PRESETS.find(
        (p) =>
          initialSecondarySettings.previewQuality &&
          p.width === initialSecondarySettings.previewQuality.maxWidth &&
          p.height === initialSecondarySettings.previewQuality.maxHeight &&
          p.quality === initialSecondarySettings.previewQuality.quality
      )
      setSelectedSecondaryQualityPreset(secondaryPreset?.key || 'custom')
      const secondaryCapturePreset = QUALITY_PRESETS.find(
        (p) =>
          initialSecondarySettings.captureQuality &&
          p.width === initialSecondarySettings.captureQuality.maxWidth &&
          p.height === initialSecondarySettings.captureQuality.maxHeight &&
          p.quality === initialSecondarySettings.captureQuality.quality
      )
      setSelectedSecondaryCaptureQualityPreset(secondaryCapturePreset?.key || 'custom')
    }
  }, [isOpen, cameraSettings, secondaryCameraSettings, selectedCamera, selectedSecondaryCamera])

  const handleCameraConfigChange = async (
    cameraKey: CameraKey,
    name: string,
    value: ConfigValue
  ): Promise<void> => {
    try {
      await unifiedCameraUtils.setConfigValue(cameraKey, name, value)
      // Refresh the config after setting a value
      const newConfig = await unifiedCameraUtils.getConfig(cameraKey)
      if (newConfig) {
        if (cameraKey === 'primary') {
          setCameraConfig(newConfig as Config)
        } else {
          setSecondaryCameraConfig(newConfig as Config)
        }
      }
    } catch (error) {
      console.error(`Failed to update ${cameraKey} camera config:`, error)
    }
  }

  const handleQualityPresetChange = (cameraKey: CameraKey, presetKey: string): void => {
    if (cameraKey === 'primary') {
      setSelectedQualityPreset(presetKey)
    } else {
      setSelectedSecondaryQualityPreset(presetKey)
    }

    if (presetKey === 'custom') return

    const preset = QUALITY_PRESETS.find((p) => p.key === presetKey)
    if (preset && preset.width && preset.height && preset.quality && preset.resizeQuality) {
      const newQuality = {
        previewQuality: {
          maxWidth: preset.width,
          maxHeight: preset.height,
          quality: preset.quality,
          resizeQuality: preset.resizeQuality as ResizeQuality
        }
      }
      if (cameraKey === 'primary') {
        setTempSettings((prev) => ({
          ...prev,
          ...newQuality
        }))
      } else {
        setTempSecondarySettings((prev) => ({
          ...prev,
          ...newQuality
        }))
      }
    }
  }

  const handleCaptureQualityPresetChange = (cameraKey: CameraKey, presetKey: string): void => {
    if (cameraKey === 'primary') {
      setSelectedCaptureQualityPreset(presetKey)
    } else {
      setSelectedSecondaryCaptureQualityPreset(presetKey)
    }

    if (presetKey === 'custom') return

    const preset = QUALITY_PRESETS.find((p) => p.key === presetKey)
    if (preset && preset.width && preset.height && preset.quality && preset.resizeQuality) {
      const newQuality = {
        captureQuality: {
          maxWidth: preset.width,
          maxHeight: preset.height,
          quality: preset.quality,
          resizeQuality: preset.resizeQuality as ResizeQuality
        }
      }
      if (cameraKey === 'primary') {
        setTempSettings((prev) => ({
          ...prev,
          ...newQuality
        }))
      } else {
        setTempSecondarySettings((prev) => ({
          ...prev,
          ...newQuality
        }))
      }
    }
  }

  const handleCameraChange = async (cameraKey: CameraKey, newCameraId: string): Promise<void> => {
    const isPrimary = cameraKey === 'primary'
    const setSelected = isPrimary ? setSelectedCamera : setSelectedSecondaryCamera
    setSelected(newCameraId)

    try {
      if (!newCameraId) {
        await unifiedCameraUtils.disconnect(cameraKey)
        if (isPrimary) {
          setCameraConfig(null)
        } else {
          setSecondaryCameraConfig(null)
        }
        return
      }

      const webcam = devices?.cameras.webcams.find((c) => c.deviceId === newCameraId)
      let deviceType: 'webcam' | 'camera' = 'webcam'
      let params: string | { productId?: number; vendorId?: number } = newCameraId

      if (webcam) {
        deviceType = 'webcam'
        params = newCameraId
      } else {
        const usbCamera = devices?.cameras.usbCameras.find((c) => `${c.productId}` === newCameraId)
        if (usbCamera) {
          deviceType = 'camera'
          params = { productId: usbCamera.productId, vendorId: usbCamera.vendorId }
        } else {
          // If device not found, maybe it's not a webcam or usb camera known to manager yet.
          // We can assume it's a webcam id for now as a fallback.
          deviceType = 'webcam'
          params = newCameraId
        }
      }

      await unifiedCameraUtils.tryToConnect(deviceType, params, cameraKey)
      const newConfig = await unifiedCameraUtils.getConfig(cameraKey)
      if (isPrimary) {
        setCameraConfig(newConfig as Config)
      } else {
        setSecondaryCameraConfig(newConfig as Config)
      }
    } catch (error) {
      console.error(`Failed to connect or configure ${cameraKey} camera:`, error)
      cogoToast.error(
        `Failed to connect to ${cameraKey} camera: ${error instanceof Error ? error.message : String(error)}`
      )
    }
  }

  if (!isOpen) return null

  const renderCameraSettings = (cameraKey: CameraKey): JSX.Element => {
    const isPrimary = cameraKey === 'primary'
    const settings = isPrimary ? tempSettings : tempSecondarySettings
    const setSettings = isPrimary ? setTempSettings : setTempSecondarySettings
    const qualityPreset = isPrimary ? selectedQualityPreset : selectedSecondaryQualityPreset
    const captureQualityPreset = isPrimary
      ? selectedCaptureQualityPreset
      : selectedSecondaryCaptureQualityPreset
    const config = isPrimary ? cameraConfig : secondaryCameraConfig
    const selectedCam = isPrimary ? selectedCamera : selectedSecondaryCamera

    return (
      <div className="space-y-6">
        <div>
          {!isPrimary && (
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('settings.camera.secondaryTitle')}
              </label>
              <div className="flex gap-2">
                <select
                  value={selectedCam || ''}
                  onChange={(e) => handleCameraChange(cameraKey, e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  title={t('settings.camera.title')}
                >
                  <option value="">{t('settings.camera.selectCamera')}</option>
                  {devices?.cameras.webcams?.map((webcam) => (
                    <option key={webcam.deviceId} value={webcam.deviceId}>
                      {webcam.label || t('settings.camera.unknownWebcam')}
                    </option>
                  ))}
                  {devices?.cameras.usbCameras?.map((camera) => (
                    <option
                      key={`${camera.vendorId}-${camera.productId}`}
                      value={`${camera.productId}`}
                    >
                      {camera.brand} (
                      {camera.hasDriver
                        ? t('settings.camera.driverStatus.installed')
                        : t('settings.camera.driverStatus.uninstalled')})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
        <div>
          <h3 className="text-xl font-semibold mb-4">
            {t('cameraSettings.appSettings', 'App Settings')}
          </h3>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  {t('cameraSettings.appSettingsHint.title', 'App Settings Guide')}
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    {t(
                      'cameraSettings.appSettingsHint.description',
                      'These settings control how the app interacts with your camera. Adjust preview quality and capture ratio to match your needs. Lower quality settings can improve performance.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('cameraSettings.previewQuality.title', 'Preview Quality')}
              </label>
              <select
                value={qualityPreset}
                onChange={(e) => handleQualityPresetChange(cameraKey, e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                title={t('cameraSettings.previewQuality.title', 'Preview Quality')}
                aria-label={t('cameraSettings.previewQuality.title', 'Preview Quality')}
              >
                {QUALITY_PRESETS.filter((p) => p.key !== 'ultra').map((preset) => (
                  <option key={preset.key} value={preset.key}>
                    {t(`cameraSettings.qualityPresets.${preset.key}`)}
                  </option>
                ))}
                <option value="custom">
                  {t('cameraSettings.previewQuality.options.custom', 'Custom')}
                </option>
              </select>

              {qualityPreset === 'custom' && (
                <div className="space-y-3 p-3 mt-2 border border-gray-200 rounded-md">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.previewQuality.maxWidth', 'Max Width')}
                    </label>
                    <input
                      type="number"
                      min="320"
                      max="1920"
                      step="80"
                      value={settings.previewQuality?.maxWidth || 640}
                      onChange={(e) => {
                        let value = parseInt(e.target.value)
                        if (value > 1920) value = 1920
                        setSettings((prev) => {
                          if (ensurePreviewQuality(prev)) {
                            const newPreviewQuality: PreviewQuality = {
                              ...prev.previewQuality,
                              maxWidth: value
                            }
                            return {
                              ...prev,
                              previewQuality: newPreviewQuality
                            }
                          }
                          return prev
                        })
                      }}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      title={t('cameraSettings.previewQuality.maxWidth', 'Max Width')}
                      aria-label={t('cameraSettings.previewQuality.maxWidth', 'Max Width')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.previewQuality.maxHeight', 'Max Height')}
                    </label>
                    <input
                      type="number"
                      min="240"
                      max="1080"
                      step="60"
                      value={settings.previewQuality?.maxHeight || 480}
                      onChange={(e) => {
                        let value = parseInt(e.target.value)
                        if (value > 1080) value = 1080
                        setSettings((prev) => {
                          if (ensurePreviewQuality(prev)) {
                            const newPreviewQuality: PreviewQuality = {
                              ...prev.previewQuality,
                              maxHeight: value
                            }
                            return {
                              ...prev,
                              previewQuality: newPreviewQuality
                            }
                          }
                          return prev
                        })
                      }}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      title={t('cameraSettings.previewQuality.maxHeight', 'Max Height')}
                      aria-label={t('cameraSettings.previewQuality.maxHeight', 'Max Height')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.previewQuality.quality', 'JPEG Quality')}
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="0.4"
                        max="1"
                        step="0.1"
                        value={settings.previewQuality?.quality || 0.8}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value)
                          setSettings((prev) => {
                            if (ensurePreviewQuality(prev)) {
                              const newPreviewQuality: PreviewQuality = {
                                ...prev.previewQuality,
                                quality: value
                              }
                              return {
                                ...prev,
                                previewQuality: newPreviewQuality
                              }
                            }
                            return prev
                          })
                        }}
                        className="flex-1"
                        title={t('cameraSettings.previewQuality.quality', 'JPEG Quality')}
                        aria-label={t('cameraSettings.previewQuality.quality', 'JPEG Quality')}
                      />
                      <span className="text-sm font-medium w-16">
                        {Math.round((settings.previewQuality?.quality || 0.8) * 100)}%
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.previewQuality.resizeQuality', 'Resize Quality')}
                    </label>
                    <div className="flex items-center gap-4">
                      <select
                        value={settings.previewQuality?.resizeQuality || 'medium'}
                        onChange={(e) => {
                          const value = e.target.value as ResizeQuality
                          setSettings((prev) => {
                            if (ensurePreviewQuality(prev)) {
                              const newPreviewQuality: PreviewQuality = {
                                ...prev.previewQuality,
                                resizeQuality: value
                              }
                              return {
                                ...prev,
                                previewQuality: newPreviewQuality
                              }
                            }
                            return prev
                          })
                        }}
                        className="flex-1"
                        title={t('cameraSettings.previewQuality.resizeQuality', 'Resize Quality')}
                        aria-label={t(
                          'cameraSettings.previewQuality.resizeQuality',
                          'Resize Quality'
                        )}
                      >
                        {QUALITY_PRESETS.map((preset) => (
                          <option key={preset.key} value={preset.resizeQuality}>
                            {t(`cameraSettings.previewQuality.options.${preset.key}`, preset.label)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 mt-1">
                {t(
                  'cameraSettings.previewQuality.description',
                  'Lower quality improves performance and reduces lag'
                )}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('cameraSettings.captureQuality.title', 'Capture Image Quality')}
              </label>
              <select
                value={captureQualityPreset}
                onChange={(e) => handleCaptureQualityPresetChange(cameraKey, e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                title={t('cameraSettings.captureQuality.title', 'Capture Image Quality')}
                aria-label={t('cameraSettings.captureQuality.title', 'Capture Image Quality')}
              >
                {QUALITY_PRESETS.map((preset) => {
                  const isUltra = preset.key === 'ultra'
                  const isEnterprise = subscriptionType === 'enterprise'
                  const isDisabled = isUltra && !isEnterprise
                  return (
                    <option key={preset.key} value={preset.key} disabled={isDisabled}>
                      {t(`cameraSettings.qualityPresets.${preset.key}`)}
                      {isUltra && isEnterprise && ` (${t('cameraSettings.qualityPresets.enterpriseOnly')})`}
                    </option>
                  )
                })}
                <option value="custom">
                  {t('cameraSettings.previewQuality.options.custom', 'Custom')}
                </option>
              </select>

              {captureQualityPreset === 'custom' && (
                <div className="space-y-3 p-3 mt-2 border border-gray-200 rounded-md">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.captureQuality.maxWidth', 'Max Width')}
                    </label>
                    <input
                      type="number"
                      min="320"
                      max="1920"
                      step="80"
                      value={settings.captureQuality?.maxWidth || 1920}
                      onChange={(e) => {
                        let value = parseInt(e.target.value)
                        if (value > 1920) value = 1920
                        setSettings((prev) => {
                          if (ensureCaptureQuality(prev)) {
                            const newCaptureQuality: PreviewQuality = {
                              ...prev.captureQuality,
                              maxWidth: value
                            }
                            return {
                              ...prev,
                              captureQuality: newCaptureQuality
                            }
                          }
                          return prev
                        })
                      }}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      title={t('cameraSettings.captureQuality.maxWidth', 'Max Width')}
                      aria-label={t('cameraSettings.captureQuality.maxWidth', 'Max Width')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.captureQuality.maxHeight', 'Max Height')}
                    </label>
                    <input
                      type="number"
                      min="240"
                      max="1080"
                      step="60"
                      value={settings.captureQuality?.maxHeight || 1080}
                      onChange={(e) => {
                        let value = parseInt(e.target.value)
                        if (value > 1080) value = 1080
                        setSettings((prev) => {
                          if (ensureCaptureQuality(prev)) {
                            const newCaptureQuality: PreviewQuality = {
                              ...prev.captureQuality,
                              maxHeight: value
                            }
                            return {
                              ...prev,
                              captureQuality: newCaptureQuality
                            }
                          }
                          return prev
                        })
                      }}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      title={t('cameraSettings.captureQuality.maxHeight', 'Max Height')}
                      aria-label={t('cameraSettings.captureQuality.maxHeight', 'Max Height')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.captureQuality.quality', 'JPEG Quality')}
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="0.4"
                        max="1"
                        step="0.1"
                        value={settings.captureQuality?.quality || 1.0}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value)
                          setSettings((prev) => {
                            if (ensureCaptureQuality(prev)) {
                              const newCaptureQuality: PreviewQuality = {
                                ...prev.captureQuality,
                                quality: value
                              }
                              return {
                                ...prev,
                                captureQuality: newCaptureQuality
                              }
                            }
                            return prev
                          })
                        }}
                        className="flex-1"
                        title={t('cameraSettings.captureQuality.quality', 'JPEG Quality')}
                        aria-label={t('cameraSettings.captureQuality.quality', 'JPEG Quality')}
                      />
                      <span className="text-sm font-medium w-16">
                        {Math.round((settings.captureQuality?.quality || 1.0) * 100)}%
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.captureQuality.resizeQuality', 'Resize Quality')}
                    </label>
                    <div className="flex items-center gap-4">
                      <select
                        value={settings.captureQuality?.resizeQuality || 'high'}
                        onChange={(e) => {
                          const value = e.target.value as ResizeQuality
                          setSettings((prev) => {
                            if (ensureCaptureQuality(prev)) {
                              const newCaptureQuality: PreviewQuality = {
                                ...prev.captureQuality,
                                resizeQuality: value
                              }
                              return {
                                ...prev,
                                captureQuality: newCaptureQuality
                              }
                            }
                            return prev
                          })
                        }}
                        className="flex-1"
                        title={t('cameraSettings.captureQuality.resizeQuality', 'Resize Quality')}
                        aria-label={t(
                          'cameraSettings.captureQuality.resizeQuality',
                          'Resize Quality'
                        )}
                      >
                        {QUALITY_PRESETS.map((preset) => (
                          <option key={preset.key} value={preset.resizeQuality}>
                            {t(`cameraSettings.previewQuality.options.${preset.key}`, preset.label)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 mt-1">
                {t(
                  'cameraSettings.captureQuality.description',
                  'Adjust resolution and quality for captured photos. Higher quality results in larger file sizes.'
                )}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('cameraSettings.captureRatio.title', 'Capture Ratio')}
              </label>
              <select
                title={t('cameraSettings.captureRatio.title', 'Capture Ratio')}
                value={settings.captureRatio || ''}
                onChange={(e) => {
                  const nextValue = e.target.value
                  let nextRatio: number | 'auto' | undefined

                  if (!nextValue || nextValue === 'auto') {
                    nextRatio = 'auto'
                  } else {
                    const parsedRatio = parseFloat(nextValue)
                    nextRatio = Number.isFinite(parsedRatio) ? parsedRatio : 'auto'
                  }

                  setSettings((prev) => ({
                    ...prev,
                    captureRatio: nextRatio
                  }))
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {RATIO_OPTIONS.map((option) => (
                  <option key={option.key} value={option.value || ''}>
                    {t(`cameraSettings.captureRatio.options.${option.key}`, option.label)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('cameraSettings.previewRotation.title', 'Preview Rotation')}
              </label>
              <p className="mb-2 text-xs text-gray-500">
                {t(
                  'cameraSettings.previewRotation.description',
                  'Rotate the live camera feed on screen. Does not affect the saved photo.'
                )}
              </p>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <span className="text-sm">
                  {t('cameraSettings.previewRotation.label', 'Live preview rotation')}
                </span>
                <select
                  value={settings.previewRotation}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      previewRotation: parseInt(e.target.value)
                    }))
                  }
                  className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  title={t('cameraSettings.previewRotation.title', 'Preview Rotation')}
                  aria-label={t('cameraSettings.previewRotation.title', 'Preview Rotation')}
                >
                  <option value={0}>0°</option>
                  <option value={90}>90°</option>
                  <option value={180}>180°</option>
                  <option value={270}>270°</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('cameraSettings.captureRotation.title', 'Capture Rotation')}
              </label>
              <p className="mb-2 text-xs text-gray-500">
                {t(
                  'cameraSettings.captureRotation.description',
                  'Rotate the saved photo pixels. Use this when your camera already applies its own rotation to shutter captures.'
                )}
              </p>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <span className="text-sm">
                  {t('cameraSettings.captureRotation.label', 'Saved image rotation')}
                </span>
                <select
                  value={settings.captureRotation}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      captureRotation: parseInt(e.target.value)
                    }))
                  }
                  className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  title={t('cameraSettings.captureRotation.title', 'Capture Rotation')}
                  aria-label={t('cameraSettings.captureRotation.title', 'Capture Rotation')}
                >
                  <option value={0}>0°</option>
                  <option value={90}>90°</option>
                  <option value={180}>180°</option>
                  <option value={270}>270°</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('cameraSettings.mirror.title', 'Mirror')}
              </label>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <div className="space-y-1">
                  <span className="text-sm">
                    {t('cameraSettings.mirror.enable', 'Mirror camera preview and captured image')}
                  </span>
                  <p className="text-xs text-gray-500">
                    {t(
                      'cameraSettings.mirror.description',
                      'Applies horizontal flip to both the live preview and saved photos. Can be combined with any rotation.'
                    )}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.mirror}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        mirror: e.target.checked
                      }))
                    }
                    aria-label={t('cameraSettings.mirror.enable', 'Toggle mirror')}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('cameraSettings.applyFilterOnPreview.title', 'Apply Filter on Preview')}
              </label>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <div className="space-y-1">
                  <span className="text-sm">
                    {t(
                      'cameraSettings.applyFilterOnPreview.enable',
                      'Apply filter effects on live preview'
                    )}
                  </span>
                  <p className="text-xs text-gray-500">
                    {t(
                      'cameraSettings.applyFilterOnPreview.description',
                      'Disabling this will improve preview performance'
                    )}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.applyFilterOnPreview}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        applyFilterOnPreview: e.target.checked
                      }))
                    }
                    aria-label={t(
                      'cameraSettings.applyFilterOnPreview.enable',
                      'Toggle filter on preview'
                    )}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('cameraSettings.useShutterCapture.title', 'Use shutter capture')}
              </label>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <div className="space-y-1">
                  <span className="text-sm">
                    {t(
                      'cameraSettings.useShutterCapture.description',
                      'Use the camera physical shutter for capturing photos. When disabled, the app will capture the current preview frame instead. Use shutter mode for better quality photos, or preview capture for faster response.'
                    )}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.useShutterCapture}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        useShutterCapture: e.target.checked
                      }))
                    }
                    aria-label={t(
                      'cameraSettings.useShutterCapture.enable',
                      'Toggle shutter capture'
                    )}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>

            {settings.useShutterCapture && (
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t(
                    'cameraSettings.autoBrightenLiveViewForFlash.title',
                    'Brighten Live Preview When Using Flash'
                  )}
                </label>
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                  <div className="space-y-1">
                    <span className="text-sm">
                      {t(
                        'cameraSettings.autoBrightenLiveViewForFlash.description',
                        'Automatically adjust camera settings (shutter speed) during live view to make the preview brighter when using flash. Original settings are restored before capture.'
                      )}
                    </span>
                    <p className="text-xs text-gray-500">
                      {t(
                        'cameraSettings.autoBrightenLiveViewForFlash.note',
                        'Only works with cameras that support shutter speed adjustment. Webcams are not supported.'
                      )}
                    </p>
                    {((isPrimary && !availableSettings?.isSupported) ||
                      (!isPrimary && !secondaryAvailableSettings?.isSupported)) && (
                      <p className="text-xs text-amber-600">
                        {t(
                          'cameraSettings.autoBrightenLiveViewForFlash.notSupported',
                          'Camera does not support automatic shutter speed adjustment'
                        )}
                      </p>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={settings.autoBrightenLiveViewForFlash}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          autoBrightenLiveViewForFlash: e.target.checked
                        }))
                      }
                      aria-label={t(
                        'cameraSettings.autoBrightenLiveViewForFlash.toggle',
                        'Toggle brighten live view for flash'
                      )}
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                  </label>
                </div>

                {settings.autoBrightenLiveViewForFlash && (
                  <div className="mt-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                    <label className="block text-xs text-gray-600 mb-2">
                      {t(
                        'cameraSettings.liveViewShutterSpeedOverride.title',
                        'Live View Shutter Speed (optional)'
                      )}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      {t(
                        'cameraSettings.liveViewShutterSpeedOverride.description',
                        'Default: 1/30s. Slower speeds = brighter preview but more motion blur. Must be between 1/4s and 1/200s (flash sync speed).'
                      )}
                    </p>
                    <select
                      value={
                        settings.liveViewShutterSpeedOverride
                          ? `1/${Math.round(1 / settings.liveViewShutterSpeedOverride)}`
                          : '1/30'
                      }
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === '1/30') {
                          setSettings((prev) => ({
                            ...prev,
                            liveViewShutterSpeedOverride: undefined
                          }))
                        } else {
                          const [num, den] = value.split('/').map(Number)
                          if (!isNaN(num) && !isNaN(den) && den !== 0) {
                            setSettings((prev) => ({
                              ...prev,
                              liveViewShutterSpeedOverride: num / den
                            }))
                          }
                        }
                      }}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      title={t(
                        'cameraSettings.liveViewShutterSpeedOverride.title',
                        'Live View Shutter Speed'
                      )}
                    >
                      <option value="1/4">{t('cameraSettings.shutterSpeed.1_4')}</option>
                      <option value="1/8">{t('cameraSettings.shutterSpeed.1_8')}</option>
                      <option value="1/15">{t('cameraSettings.shutterSpeed.1_15')}</option>
                      <option value="1/30">{t('cameraSettings.shutterSpeed.1_30')}</option>
                      <option value="1/60">{t('cameraSettings.shutterSpeed.1_60')}</option>
                      <option value="1/125">{t('cameraSettings.shutterSpeed.1_125')}</option>
                      <option value="1/200">{t('cameraSettings.shutterSpeed.1_200')}</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('cameraSettings.useWebcam.title', 'Use Webcam')}
              </label>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <div className="space-y-1">
                  <span className="text-sm">
                    {t(
                      'cameraSettings.useWebcam.description',
                      'Enable webcam support for this app'
                    )}
                  </span>
                  <p className="text-xs text-gray-500">
                    {t(
                      'cameraSettings.useWebcam.note',
                      'Disabling this will prevent webcam access and may help with camera conflicts'
                    )}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={useWebcam}
                    onChange={(e) => {
                      setUseWebcam(e.target.checked)
                      setTimeout(() => fetchDevices(), 100)
                    }}
                    aria-label={t('cameraSettings.useWebcam.toggle', 'Toggle webcam support')}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t(
                  'cameraSettings.ejectCameraWhenIdle.title',
                  'Eject Camera When Idle (Experimental)'
                )}
              </label>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <div className="space-y-1">
                  <span className="text-sm">
                    {t(
                      'cameraSettings.ejectCameraWhenIdle.description',
                      'Automatically disconnect the camera after 1 minute of inactivity on the welcome page to prevent camera exhaustion during long idle periods.'
                    )}
                  </span>
                  <p className="text-xs text-gray-500">
                    {t(
                      'cameraSettings.ejectCameraWhenIdle.note',
                      'Experimental feature - camera will reconnect automatically when needed'
                    )}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={ejectCameraWhenIdle}
                    onChange={(e) => {
                      setEjectCameraWhenIdle(e.target.checked)
                    }}
                    aria-label={t(
                      'cameraSettings.ejectCameraWhenIdle.toggle',
                      'Toggle eject camera when idle'
                    )}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t(
                  'cameraSettings.showCameraQualityWarnings.title',
                  'Show Camera Quality Warnings'
                )}
              </label>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <div className="space-y-1">
                  <span className="text-sm">
                    {t(
                      'cameraSettings.showCameraQualityWarnings.description',
                      'Automatically show warnings when camera detects dark images, black images, or poor quality. Disable this if your photobooth room is intentionally dark before capture.'
                    )}
                  </span>
                  <p className="text-xs text-gray-500">
                    {t(
                      'cameraSettings.showCameraQualityWarnings.note',
                      'When disabled, quality warnings will not appear automatically'
                    )}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={showCameraQualityWarnings}
                    onChange={(e) => {
                      setShowCameraQualityWarnings(e.target.checked)
                    }}
                    aria-label={t(
                      'cameraSettings.showCameraQualityWarnings.toggle',
                      'Toggle camera quality warnings'
                    )}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {config && (
          <div>
            <h3 className="text-xl font-semibold mb-4">
              {t('cameraSettings.cameraConfig', 'Camera Configuration')}
            </h3>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    {t('cameraSettings.cameraConfigWarning.title', 'Camera Configuration Warning')}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      {t(
                        'cameraSettings.cameraConfigWarning.description',
                        'Camera settings may vary between different camera models. Some settings might not be adjustable through this software. Please test each setting to ensure it works with your camera.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <CameraConfigWidget
                config={config}
                onChange={(name, value) => handleCameraConfigChange(cameraKey, name, value)}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 sm:p-6">
      <div className="flex h-[90vh] max-h-[min(92dvh,calc(100dvh-2rem))] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div
          className={`flex-grow overflow-y-auto ${
            camModalDense
              ? 'p-3 [&_.mb-4]:!mb-2 [&_.mb-6]:!mb-3 [&_.mt-2]:!mt-1 [&_.space-y-4]:!space-y-2 [&_.p-4]:!p-2.5 [&_h3]:!mb-2 [&_h3]:!text-sm [&_h3]:!font-semibold [&_label]:!mb-1 [&_label]:!text-[11px] [&_select]:!py-1.5 [&_select]:!text-xs [&_input]:!py-1.5 [&_input]:!text-xs [&_.text-xl]:!text-base [&_.text-sm]:!text-xs'
              : 'p-4 sm:p-8'
          }`}
        >
          <h2
            className={`mb-4 font-bold ${camModalDense ? 'text-base' : 'text-lg sm:mb-6 sm:text-2xl'}`}
          >
            {t('cameraSettings.title', 'Camera Settings')}
          </h2>

          <div className={`flex border-b border-gray-200 ${camModalDense ? 'mb-3' : 'mb-4 sm:mb-6'}`}>
            <button
              type="button"
              onClick={() => setActiveTab('primary')}
              className={`font-medium ${camModalDense ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} ${
                activeTab === 'primary'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('cameraSettings.primary')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('secondary')}
              className={`font-medium ${camModalDense ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} ${
                activeTab === 'secondary'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('cameraSettings.secondary')}
            </button>
          </div>

          {activeTab === 'primary' ? renderCameraSettings('primary') : null}
          {activeTab === 'secondary' ? renderCameraSettings('secondary') : null}
        </div>

        <div
          className={`flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-white px-4 sm:gap-3 ${camModalDense ? 'py-2' : 'py-3 sm:py-4'}`}
        >
          <Button
            text={t('common.cancel', 'Cancel')}
            variant="secondary"
            onClick={onClose}
            className={camModalDense ? '!px-3 !py-1.5 !text-xs' : '!px-4 !py-2 !text-sm sm:!px-6 sm:!text-base'}
          />
          <Button
            text={t('common.save', 'Save')}
            onClick={() => {
              setCameraSettings(tempSettings)
              setSecondaryCameraSettings(tempSecondarySettings)
              onClose()
            }}
            className={camModalDense ? '!px-3 !py-1.5 !text-xs' : '!px-4 !py-2 !text-sm sm:!px-6 sm:!text-base'}
          />
        </div>
      </div>
    </div>
  )
}

export default CameraSettingsModal
